/* eslint-disable max-lines-per-function */
/**
 * Mapbox GL Instance Singleton Hook
 *
 * This hook implements a singleton pattern for the Mapbox map instance,
 * allowing it to persist across route changes for optimal performance.
 *
 * Key benefits:
 * - WebGL context stays alive (no destroy/recreate on navigation)
 * - Tiles remain cached in memory
 * - Instant transitions between routes (<16ms)
 *
 * @see docs/plans/prototype/MAPBOX_HYBRID_IMPLEMENTATION.md
 */

import mapboxgl from "mapbox-gl";
import React from "react";
import { MAPBOX_CONFIG } from "../../utils/mapbox/config";

// Singleton map instance - persists across route changes
let mapInstance: mapboxgl.Map | null = null;
// Promise used to serialize map creation to prevent race conditions
let mapCreationPromise: Promise<mapboxgl.Map> | null = null;

/**
 * Helper: Handle successful map initialization after tiles load
 */
function handleMapTilesLoaded(createdMap: mapboxgl.Map, resolve: (map: mapboxgl.Map) => void): void {
  resolve(createdMap);
}

/**
 * Helper: Setup map load handler with tile loading
 */
function setupMapLoadHandler(createdMap: mapboxgl.Map, resolve: (map: mapboxgl.Map) => void): void {
  createdMap.on("load", () => {
    // Add custom satellite tiles source
    createdMap.addSource(MAPBOX_CONFIG.customTiles.sourceId, {
      type: "raster",
      tiles: [MAPBOX_CONFIG.customTiles.tilesUrl],
      tileSize: MAPBOX_CONFIG.customTiles.tileSize,
      maxzoom: MAPBOX_CONFIG.customTiles.maxZoom,
    });

    // Add satellite tiles layer
    createdMap.addLayer({
      id: MAPBOX_CONFIG.customTiles.layerId,
      type: "raster",
      source: MAPBOX_CONFIG.customTiles.sourceId,
      paint: {
        "raster-opacity": 0.3, // Default dimmed state
      },
    });

    // Wait for tiles to actually load before resolving
    // "idle" event fires when all tiles/sources have finished loading
    createdMap.once("idle", () => handleMapTilesLoaded(createdMap, resolve));
  });
}

/**
 * Helper: Resize map after creation
 */
function resizeMapAfterCreation(map: mapboxgl.Map): void {
  try {
    map.resize();
    // Double-check after another frame to ensure container has fully rendered
    requestAnimationFrame(() => {
      try {
        map.resize();
      } catch (err) {
        console.warn("Warning: failed second resize after creation:", err);
      }
    });
  } catch (err) {
    console.warn("Warning: failed to resize Mapbox after creation:", err);
  }
}

/**
 * Hook return type
 */
interface UseMapboxInstanceReturn {
  containerRef: React.RefObject<HTMLDivElement | null>;
  map: mapboxgl.Map | null;
  isLoaded: boolean;
}

/**
 * Custom hook for accessing the singleton Mapbox GL map instance
 *
 * Usage:
 * ```tsx
 * const { containerRef, map, isLoaded } = useMapboxInstance();
 *
 * return <div ref={containerRef} className="w-full h-full" />;
 * ```
 *
 * The hook will:
 * 1. Create a new map instance if none exists
 * 2. Reuse the existing instance if available
 * 3. Move the map to the new container if container changes
 * 4. Keep the instance alive when component unmounts
 */
export function useMapboxInstance(): UseMapboxInstanceReturn {
  const containerRef = React.useRef<HTMLDivElement>(null);
  const [map, setMap] = React.useState<mapboxgl.Map | null>(mapInstance);
  const [isLoaded, setIsLoaded] = React.useState(false);

  // Add ResizeObserver to handle container size changes
  React.useEffect(() => {
    if (!containerRef.current || !mapInstance) return;

    const resizeObserver = new ResizeObserver(() => {
      if (mapInstance) {
        try {
          mapInstance.resize();
        } catch (err) {
          console.warn("Warning: ResizeObserver resize failed:", err);
        }
      }
    });

    resizeObserver.observe(containerRef.current);

    return () => {
      resizeObserver.disconnect();
    };
  }, [map]);

  React.useEffect(() => {
    if (!containerRef.current) return;

    // If an instance exists already, move its container into our ref (if needed)
    const attachExistingInstance = () => {
      if (!mapInstance) return;

      // Check loaded state before moving container
      const wasLoaded = mapInstance.loaded();
      const existingContainer = mapInstance.getContainer();

      // If different container, append the existing container into new parent.
      // Do NOT call remove() on the container node (it may break internal Mapbox references).
      if (existingContainer.parentElement !== containerRef.current) {
        try {
          // Remove from old parent first
          if (existingContainer.parentElement) {
            existingContainer.remove();
          }

          // Ensure the incoming parent has sensible sizing (caller is responsible for layout)
          // but force the container to fill its new parent to avoid clipped rendering
          existingContainer.style.width = "100%";
          existingContainer.style.height = "100%";

          containerRef.current!.appendChild(existingContainer);

          // Give the browser a frame to apply layout, then notify Mapbox to resize its canvas
          // This addresses intermittent cases where the map appears blank or clipped after
          // being moved between different layout containers.
          requestAnimationFrame(() => {
            try {
              mapInstance!.resize();
            } catch (err) {
              // Non-fatal - log for diagnostics

              console.warn("Warning: failed to resize Mapbox after reattach:", err);
            }
          });
        } catch {
          // In rare cases the node may already be detached; continue silently
        }
      }

      setMap(mapInstance);

      // Use the loaded state from before we moved the container
      // Moving the container can temporarily make loaded() return false
      if (wasLoaded) {
        setIsLoaded(true);
      } else {
        // Wait for the map to load
        mapInstance.once("load", () => {
          setIsLoaded(true);
        });
      }
    };

    if (mapInstance) {
      attachExistingInstance();
      return;
    }

    // Serialize creation so simultaneous mounts await the same instance
    mapCreationPromise ??= new Promise<mapboxgl.Map>((resolve, reject) => {
      try {
        mapboxgl.accessToken = MAPBOX_CONFIG.accessToken;

        const createdMap = new mapboxgl.Map({
          container: containerRef.current!,
          style: MAPBOX_CONFIG.style,
          center: MAPBOX_CONFIG.defaultView.center,
          zoom: MAPBOX_CONFIG.defaultView.zoom,
          minZoom: MAPBOX_CONFIG.defaultView.minZoom,
          maxZoom: MAPBOX_CONFIG.defaultView.maxZoom,
          projection: MAPBOX_CONFIG.projection,
          renderWorldCopies: MAPBOX_CONFIG.renderWorldCopies,
          attributionControl: false, // Disabled - using custom attribution instead
          trackResize: false, // Disable telemetry/analytics
          collectResourceTiming: false, // Disable performance telemetry
        });

        // Add custom attribution control with only NASA credit (no Mapbox branding)
        createdMap.addControl(
          new mapboxgl.AttributionControl({
            customAttribution: "© Mapbox © OpenStreetMap | NASA Earth Observatory",
            compact: true,
          }),
          "bottom-right",
        );

        setupMapLoadHandler(createdMap, resolve);

        createdMap.on("error", (e: Error) => {
          // If an error occurs during creation, log it and reject

          console.error("Mapbox GL error during creation:", e);
          reject(e);
        });
      } catch (err) {
        reject(err as Error);
      }
    });

    // Await the creation promise and set state
    let cancelled = false;
    void mapCreationPromise
      .then((createdMap) => {
        if (cancelled) return;

        // Store as singleton
        mapInstance = createdMap;

        // Map container is already attached to containerRef.current from creation
        // No need to move it - it's already in the right place

        console.log("🗺️ Mapbox: Created new map instance, loaded:", mapInstance.loaded());
        setMap(mapInstance);
        setIsLoaded(mapInstance.loaded());
        // Ensure map sizing is correct for the current container
        requestAnimationFrame(() => resizeMapAfterCreation(mapInstance!));
      })
      .catch((err) => {
        console.error("Failed to create Mapbox instance:", err);
      });

    return () => {
      // If effect is cleaned up before creation resolves, note cancellation
      // (we intentionally do not destroy the created map here to preserve singleton)

      cancelled = true;
    };
  }, []); // Empty deps - only runs once

  return { containerRef, map, isLoaded };
}

/**
 * Utility to reset the map instance
 * Useful for:
 * - Testing (reset between tests)
 * - Feature flag toggle (clean slate when switching implementations)
 * - Error recovery (restart map if corrupted)
 *
 * ⚠️ Warning: This will destroy the WebGL context and clear all tile caches
 */
export function resetMapInstance(): void {
  if (mapInstance) {
    mapInstance.remove();
    mapInstance = null;
  }
}

/**
 * Get the current map instance (if it exists)
 * Useful for debugging or accessing map outside of React components
 */
export function getMapInstance(): mapboxgl.Map | null {
  return mapInstance;
}

// Expose map instance to window for debugging
if (globalThis.window !== undefined) {
  (globalThis as unknown as { __getMapInstance: () => mapboxgl.Map | null }).__getMapInstance = getMapInstance;
}
